import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './YourMonthlyWinsPage.module.css';
import {
  fetchQuickRichuelsAndCheckInHistory,
  handleApiError,
} from '../../utils/api';
import { FINANCIAL_CONSTANTS, getFirstNameFromStorage } from '../../utils/helper';
import ForecastProgressFooter from './forecast/ForecastProgressFooter';
import { SEE_ALL_MONEY_MOVES_PATH } from './your-quick-richuels/constants';
import { buildYourMonthlyWinsData } from '../../utils/checkInUtils';
import {
  getCareerShiftRunwayData,
  normalizeOnboardingProgressKeys,
  onboardingProgressFromV2OnboardingAiResult,
} from '../../utils/onboardingUtils';

const HOME_ROUTE = '/dashboard/forecast';
const GRID_TICKS = [0.25, 0.5, 0.75, 1];

const CHART = {
  width: 480,
  height: 220,
  padLeft: 40,
  padRight: 24,
  padTop: 20,
  padBottom: 36,
};
const CHART_PLOT_W = CHART.width - CHART.padLeft - CHART.padRight;
const CHART_PLOT_H = CHART.height - CHART.padTop - CHART.padBottom;
const CHART_RIGHT = CHART.width - CHART.padRight;
const CHART_LABEL_Y = CHART.height - 10;

const cx = (...parts) => parts.filter(Boolean).join(' ');

function chartX(index, count) {
  const slot = count > 1 ? CHART_PLOT_W / (count - 1) : CHART_PLOT_W / 2;
  return CHART.padLeft + index * slot;
}

function chartY(value, maxVal) {
  const safeMax = Math.max(maxVal, 1);
  const clamped = Math.max(0, Math.min(safeMax, value));
  return CHART.padTop + CHART_PLOT_H - (clamped / safeMax) * CHART_PLOT_H;
}

function buildLinePath(values, maxVal) {
  if (!values?.length) return '';
  const n = values.length;
  const pts = values.map((v, i) => [chartX(i, n), chartY(v, maxVal)]);
  if (n === 1) return `M ${pts[0][0]},${pts[0][1]}`;
  if (n === 2) return `M ${pts[0][0]},${pts[0][1]} L ${pts[1][0]},${pts[1][1]}`;

  let path = `M ${pts[0][0]},${pts[0][1]}`;
  for (let i = 1; i < n; i += 1) {
    const p0 = pts[Math.max(0, i - 2)];
    const p1 = pts[i - 1];
    const p2 = pts[i];
    const p3 = pts[Math.min(n - 1, i + 1)];
    const cp1x = (p1[0] + (p2[0] - p0[0]) / 6).toFixed(2);
    const cp1y = (p1[1] + (p2[1] - p0[1]) / 6).toFixed(2);
    const cp2x = (p2[0] - (p3[0] - p1[0]) / 6).toFixed(2);
    const cp2y = (p2[1] - (p3[1] - p1[1]) / 6).toFixed(2);
    path += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2[0].toFixed(2)},${p2[1].toFixed(2)}`;
  }
  return path;
}

function TrophyIcon() {
  return (
    <svg viewBox="0 0 24 24" width="36" height="36" fill="currentColor" aria-hidden="true">
      <path d="M19 5h-2V3H7v2H5c-1.1 0-2 .9-2 2v1c0 2.55 1.92 4.63 4.39 4.94.63 1.5 1.98 2.63 3.61 2.96V19H7v2h10v-2h-4v-3.1c1.63-.33 2.98-1.46 3.61-2.96C19.08 12.63 21 10.55 21 8V7c0-1.1-.9-2-2-2zM5 8V7h2v3.82C5.84 10.4 5 9.3 5 8zm14 0c0 1.3-.84 2.4-2 2.82V7h2v1z" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
      <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" />
    </svg>
  );
}

const GrowthChart = memo(function GrowthChart({ chartMonths, hasRunwayFromProfile }) {
  const n = chartMonths.length;
  const confidenceValues = chartMonths.map((month) => month.confidence);
  const maxVal = Math.max(
    ...chartMonths.flatMap((month) => (
      month.runway != null ? [month.runway, month.confidence] : [month.confidence]
    )),
    1,
  ) * 1.12;
  const barWidth = n > 0 ? Math.min(52, (CHART_PLOT_W / n) * 0.42) : 0;
  const points = chartMonths.map((month, i) => ({
    label: month.label,
    x: chartX(i, n),
    runwayY: month.runway == null ? null : chartY(month.runway, maxVal),
    confidenceY: chartY(month.confidence, maxVal),
    delay: `${i * 90}ms`,
  }));

  return (
    <svg
      className={styles.chartSvg}
      viewBox={`0 0 ${CHART.width} ${CHART.height}`}
      role="img"
      aria-label={
        hasRunwayFromProfile
          ? 'Runway and confidence growth over the last four months'
          : 'Confidence trend over the last four months'
      }
    >
      {GRID_TICKS.map((tick) => {
        const y = chartY(maxVal * tick, maxVal);
        return (
          <line
            key={tick}
            x1={CHART.padLeft}
            y1={y}
            x2={CHART_RIGHT}
            y2={y}
            className={styles.chartGridLine}
          />
        );
      })}

      {points.map((point) => (
        point.runwayY == null ? null : (
          <rect
            key={`bar-${point.label}`}
            x={point.x - barWidth / 2}
            y={point.runwayY}
            width={barWidth}
            height={CHART.height - CHART.padBottom - point.runwayY}
            rx="6"
            className={styles.chartBar}
            style={{ '--bar-delay': point.delay }}
          />
        )
      ))}

      <path
        d={buildLinePath(confidenceValues, maxVal)}
        pathLength="1"
        className={styles.chartLineConfidence}
        fill="none"
      />

      {points.map((point) => (
        <g key={point.label}>
          <circle
            cx={point.x}
            cy={point.confidenceY}
            r="5"
            className={styles.chartPointConfidence}
            style={{ '--point-delay': point.delay }}
          />
          <text
            x={point.x}
            y={CHART_LABEL_Y}
            textAnchor="middle"
            className={styles.chartMonthLabel}
          >
            {point.label}
          </text>
        </g>
      ))}
    </svg>
  );
});

function resolveRunwayFromV2Result(cached) {
  const progress = onboardingProgressFromV2OnboardingAiResult(cached)
    || cached?.onboardingProgress
    || null;
  if (!progress) return null;

  const runway = getCareerShiftRunwayData(normalizeOnboardingProgressKeys(progress));
  if (runway.runwayValue === FINANCIAL_CONSTANTS.FULLY_COVERED) {
    return {
      months: 12,
      displayLabel: FINANCIAL_CONSTANTS.FULLY_COVERED,
    };
  }

  const parsed = parseFloat(runway.runwayValue);
  return Number.isFinite(parsed) && parsed > 0
    ? { months: parsed, displayLabel: runway.runwayDisplayValue }
    : null;
}

function deriveMonthlyWinsView(payload) {
  const runway = resolveRunwayFromV2Result(payload.v2_onboarding_ai_result);
  return buildYourMonthlyWinsData({
    checkInHistory: payload.content_reflections ?? [],
    runwayMonths: runway?.months ?? null,
    runwayDisplayLabel: runway?.displayLabel ?? null,
  });
}

function PageFrame({ children }) {
  return (
    <div className={styles.yourMonthlyWinsPage} data-page="your-monthly-wins">
      {children}
    </div>
  );
}

function LoadingState() {
  return (
    <PageFrame>
      <div className={styles.loadingState} aria-busy="true" aria-live="polite">
        <span className={styles.loadingSpinner} aria-hidden="true">
          <span className={styles.loadingSpinnerInner} />
        </span>
        <p className={styles.loadingText}>Preparing your monthly wins…</p>
      </div>
    </PageFrame>
  );
}

function ErrorState({ onRetry, onGoHome }) {
  return (
    <PageFrame>
      <div className={styles.mainContent}>
        <section className={styles.errorState} aria-live="polite">
          <h1 className={styles.heroTitle}>Your Monthly Wins</h1>
          <p className={styles.errorText}>
            We couldn&apos;t load your monthly wins right now. Check your connection and try again.
          </p>
          <div className={styles.errorActions}>
            <button type="button" className={styles.retryBtn} onClick={onRetry}>
              Try again
            </button>
            <button type="button" className={styles.finishBtn} onClick={onGoHome}>
              Go to Home
            </button>
          </div>
        </section>
      </div>
    </PageFrame>
  );
}

function YourMonthlyWinsPage() {
  const navigate = useNavigate();
  const firstName = getFirstNameFromStorage() || 'there';
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [reportPayload, setReportPayload] = useState(null);

  useEffect(() => {
    const ac = new AbortController();
    const { signal } = ac;

    const run = async () => {
      setLoading(true);
      setLoadError(false);

      try {
        const result = await fetchQuickRichuelsAndCheckInHistory(signal);
        if (signal.aborted) return;
        if (!result) {
          setLoadError(true);
          setReportPayload(null);
          return;
        }
        setReportPayload(result);
      } catch (e) {
        if (e?.name === 'AbortError' || signal.aborted) return;
        handleApiError(e, navigate, false);
        setLoadError(true);
        setReportPayload(null);
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    };

    run();
    return () => ac.abort();
  }, [navigate, reloadKey]);

  const report = useMemo(
    () => (reportPayload ? deriveMonthlyWinsView(reportPayload) : null),
    [reportPayload],
  );

  const handleGoHome = useCallback(() => {
    navigate(HOME_ROUTE);
  }, [navigate]);

  const handleRetry = useCallback(() => {
    setReloadKey((key) => key + 1);
  }, []);

  if (loading) return <LoadingState />;
  if (loadError || !report) return <ErrorState onRetry={handleRetry} onGoHome={handleGoHome} />;

  const { stats } = report;
  const statItems = [
    { label: 'Richuels Kept', value: stats.richuelsKept },
    { label: 'Reflections', value: stats.monthReflectionCount },
    { label: 'Savings Increase', value: stats.savingsIncreaseLabel },
    {
      label: 'Confidence Score',
      value: stats.confidenceScore != null ? `${stats.confidenceScore}/10` : '—',
    },
  ];

  return (
    <PageFrame>
      <div className={styles.mainContent}>
        <section className={styles.heroCard} aria-label="Your monthly wins">
          <div className={styles.heroIconWrap}>
            <TrophyIcon />
          </div>
          <p className={styles.heroPeriod}>{report.monthLabel}</p>
          <h1 className={styles.heroTitle}>{report.heroTitle}</h1>
          <p className={styles.heroSubtitle}>{report.heroSubtitle}</p>
        </section>

        <div className={styles.contentGrid}>
          <section className={styles.chartCard} aria-label="Runway and confidence growth">
            <div className={styles.chartCardHeader}>
              <div>
                <h2 className={styles.chartTitle}>More Money = More Confidence</h2>
                <p className={styles.chartSubtitle}>
                  {report.hasRunwayFromProfile
                    ? 'Your savings runway and personal confidence index over the last four months.'
                    : 'Your confidence index over the last four months. Complete your financial profile to unlock runway trends.'}
                </p>
              </div>
              <ul className={styles.legend}>
                {report.hasRunwayFromProfile ? (
                  <li>
                    <span className={cx(styles.legendSwatch, styles.legendRunway)} />
                    Runway
                  </li>
                ) : null}
                <li>
                  <span className={cx(styles.legendSwatch, styles.legendConfidence)} />
                  Confidence
                </li>
              </ul>
            </div>
            <div className={styles.chartWrap}>
              <GrowthChart
                chartMonths={report.chartMonths}
                hasRunwayFromProfile={report.hasRunwayFromProfile}
              />
            </div>
            {!report.hasConfidenceData && (
              <p className={styles.chartNote}>
                Confidence points are estimated until you reflect after more finished Quick Richuels this month.
              </p>
            )}
          </section>

          <aside className={styles.sideColumn}>
            <section className={styles.summaryCard} aria-label="Monthly summary">
              <div className={styles.summaryHeader}>
                <span className={styles.summaryIconWrap}>
                  <ShieldIcon />
                </span>
                <h2 className={styles.summaryTitle}>Summary</h2>
              </div>
              <p className={styles.summaryText}>{report.summaryText}</p>
            </section>
          </aside>
        </div>

        <section className={styles.statsCard} aria-label="Monthly stats">
          <span className={styles.statsLabel}>MONTHLY STATS</span>
          <dl className={styles.statsList}>
            {statItems.map((item) => (
              <div key={item.label} className={styles.statsRow}>
                <dt>{item.label}</dt>
                <dd>{item.value}</dd>
              </div>
            ))}
          </dl>
        </section>

        <div className={styles.footerReveal}>
          <ForecastProgressFooter
            title="Keep going!"
            subtitle={`Every small action compounds into a brighter tomorrow. You're doing great, ${firstName}!`}
            onStartMove={() => navigate(SEE_ALL_MONEY_MOVES_PATH)}
            ariaLabel="Keep going"
          />
        </div>
      </div>
    </PageFrame>
  );
}

export default YourMonthlyWinsPage;
